import { useCallback, useEffect, useState } from 'react';

const DEFAULT_TRIGGER_SELECTOR = 'button[aria-haspopup="dialog"]';
const DEFAULT_FOCUSABLE_SELECTOR = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const usePopover = ({
  wrapperRef,
  panelRef,
  isExternalInside,
  triggerSelector = DEFAULT_TRIGGER_SELECTOR,
  initialFocusSelector = DEFAULT_FOCUSABLE_SELECTOR,
} = {}) => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    const trigger = wrapperRef?.current?.querySelector(triggerSelector);
    if (trigger) trigger.focus();
  }, [wrapperRef, triggerSelector]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    const onPointerDown = (e) => {
      const wrapper = wrapperRef?.current;
      if (!wrapper) return;
      if (wrapper.contains(e.target)) return;
      if (isExternalInside && isExternalInside(e.target)) return;
      close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close, wrapperRef, isExternalInside]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef?.current;
    if (!panel) return;
    const focusable = panel.querySelector(initialFocusSelector);
    if (focusable) focusable.focus();
  }, [open, panelRef, initialFocusSelector]);

  return { open, setOpen, close };
};

export default usePopover;
